export interface LatLon {
  lat: number;
  lon: number;
}

export const EARTH_RADIUS_KM = 6371.0088;
export const KM_PER_MI = 1.609344;
/** Equatorial circumference, used for "times around the Earth". */
export const EARTH_CIRCUMFERENCE_MI = 24_901;

const toRad = (d: number) => (d * Math.PI) / 180;
const toDeg = (r: number) => (r * 180) / Math.PI;

/** Great-circle distance in kilometers. */
export function distanceKm(a: LatLon, b: LatLon): number {
  const dLat = toRad(b.lat - a.lat);
  const dLon = toRad(b.lon - a.lon);
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLon / 2) ** 2;
  return 2 * EARTH_RADIUS_KM * Math.asin(Math.min(1, Math.sqrt(h)));
}

export function distanceMi(a: LatLon, b: LatLon): number {
  return distanceKm(a, b) / KM_PER_MI;
}

/** Initial bearing from a to b in degrees, 0 = north, clockwise. */
export function bearing(a: LatLon, b: LatLon): number {
  const φ1 = toRad(a.lat);
  const φ2 = toRad(b.lat);
  const Δλ = toRad(b.lon - a.lon);
  const y = Math.sin(Δλ) * Math.cos(φ2);
  const x = Math.cos(φ1) * Math.sin(φ2) - Math.sin(φ1) * Math.cos(φ2) * Math.cos(Δλ);
  return (toDeg(Math.atan2(y, x)) + 360) % 360;
}

/** Point at fraction f (0..1) along the great circle from a to b. */
export function interpolateGreatCircle(a: LatLon, b: LatLon, f: number): LatLon {
  const φ1 = toRad(a.lat);
  const λ1 = toRad(a.lon);
  const φ2 = toRad(b.lat);
  const λ2 = toRad(b.lon);
  const δ = distanceKm(a, b) / EARTH_RADIUS_KM;
  if (δ < 1e-9) return { lat: a.lat, lon: a.lon };
  const A = Math.sin((1 - f) * δ) / Math.sin(δ);
  const B = Math.sin(f * δ) / Math.sin(δ);
  const x = A * Math.cos(φ1) * Math.cos(λ1) + B * Math.cos(φ2) * Math.cos(λ2);
  const y = A * Math.cos(φ1) * Math.sin(λ1) + B * Math.cos(φ2) * Math.sin(λ2);
  const z = A * Math.sin(φ1) + B * Math.sin(φ2);
  return { lat: toDeg(Math.atan2(z, Math.sqrt(x * x + y * y))), lon: toDeg(Math.atan2(y, x)) };
}

/** Great-circle path from a to b as `segments + 1` points. */
export function greatCirclePath(a: LatLon, b: LatLon, segments = 64): LatLon[] {
  const pts: LatLon[] = [];
  for (let i = 0; i <= segments; i++) pts.push(interpolateGreatCircle(a, b, i / segments));
  return pts;
}

/**
 * Makes longitudes continuous along a path so lines don't jump across the map at the
 * antimeridian (179 -> -179 becomes 179 -> 181).
 */
export function unwrapLongitudes(points: LatLon[]): LatLon[] {
  const out: LatLon[] = [];
  let offset = 0;
  for (let i = 0; i < points.length; i++) {
    const p = points[i]!;
    if (i > 0) {
      const prev = points[i - 1]!;
      const d = p.lon - prev.lon;
      if (d > 180) offset -= 360;
      else if (d < -180) offset += 360;
    }
    out.push({ lat: p.lat, lon: p.lon + offset });
  }
  return out;
}

/** Total length of a polyline in kilometers. */
export function pathLengthKm(points: LatLon[]): number {
  let total = 0;
  for (let i = 1; i < points.length; i++) total += distanceKm(points[i - 1]!, points[i]!);
  return total;
}

/** Point at a distance (km) along a polyline, with the heading of that segment. */
export function pointAlongPath(points: LatLon[], km: number): { point: LatLon; heading: number } {
  if (points.length === 0) throw new Error('Empty path');
  if (points.length === 1 || km <= 0) {
    const b = points[1] ?? points[0]!;
    return { point: points[0]!, heading: bearing(points[0]!, b) };
  }
  let remaining = km;
  for (let i = 1; i < points.length; i++) {
    const a = points[i - 1]!;
    const b = points[i]!;
    const seg = distanceKm(a, b);
    if (remaining <= seg || i === points.length - 1) {
      const f = seg === 0 ? 0 : Math.min(1, remaining / seg);
      return { point: interpolateGreatCircle(a, b, f), heading: bearing(a, b) };
    }
    remaining -= seg;
  }
  const last = points[points.length - 1]!;
  return { point: last, heading: bearing(points[points.length - 2]!, last) };
}

export interface Bounds {
  minLat: number;
  maxLat: number;
  minLon: number;
  maxLon: number;
}

/** Bounds of already-unwrapped points. */
export function boundsOf(points: LatLon[]): Bounds {
  let minLat = 90;
  let maxLat = -90;
  let minLon = Infinity;
  let maxLon = -Infinity;
  for (const p of points) {
    minLat = Math.min(minLat, p.lat);
    maxLat = Math.max(maxLat, p.lat);
    minLon = Math.min(minLon, p.lon);
    maxLon = Math.max(maxLon, p.lon);
  }
  return { minLat, maxLat, minLon, maxLon };
}

/** Web Mercator y for a latitude, in radians-scale units. */
export function mercatorY(lat: number): number {
  const φ = toRad(Math.max(-85, Math.min(85, lat)));
  return Math.log(Math.tan(Math.PI / 4 + φ / 2));
}

/**
 * A simple Mercator fit of bounds into a width x height box with padding, returning a
 * projector. Used by SVG mini-maps and arc thumbnails.
 */
export function fitMercator(
  b: Bounds,
  width: number,
  height: number,
  padding = 0.12,
  minSpanDeg = 4,
): (p: LatLon) => [number, number] {
  let { minLat, maxLat, minLon, maxLon } = b;
  const lonSpan = Math.max(maxLon - minLon, minSpanDeg);
  const latSpan = Math.max(maxLat - minLat, minSpanDeg / 2);
  const cLon = (minLon + maxLon) / 2;
  const cLat = (minLat + maxLat) / 2;
  minLon = cLon - lonSpan / 2;
  maxLon = cLon + lonSpan / 2;
  minLat = Math.max(-80, cLat - latSpan / 2);
  maxLat = Math.min(80, cLat + latSpan / 2);
  const x0 = toRad(minLon);
  const x1 = toRad(maxLon);
  const y0 = mercatorY(minLat);
  const y1 = mercatorY(maxLat);
  const innerW = width * (1 - padding * 2);
  const innerH = height * (1 - padding * 2);
  const scale = Math.min(innerW / (x1 - x0), innerH / (y1 - y0));
  const offX = (width - (x1 - x0) * scale) / 2;
  const offY = (height - (y1 - y0) * scale) / 2;
  return (p) => [offX + (toRad(p.lon) - x0) * scale, offY + (y1 - mercatorY(p.lat)) * scale];
}
